import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Grid, Box, Paper, MenuItem, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, FormControlLabel, AppBar, Toolbar, CircularProgress, Backdrop, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, Divider, Chip } from '@mui/material';
import { AccountBalanceWalletOutlined as GenerateIcon, ReplayOutlined as ResetIcon } from '@mui/icons-material';
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from 'react-router-dom';
import base64 from 'base64-js';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ─────────────────────────────────────────────
// Info data for the dialog
// ─────────────────────────────────────────────
const feeGenerationInfoSections = [
  {
    title: "Class & Session Selection",
    color: "#1976D2",
    description: "Select the class and academic session for which you want to generate fees. The session auto-selects the current financial year on load.",
    fields: [
      "Class – Select the class to generate fees for",
      "Session – Academic year (auto-selected to current financial year)",
      "Selecting a class triggers automatic loading of all 12 months' charge data",
    ],
  },
  {
  title: "Student Type",
  color: "#00796B",
  description: "Choose which group of students the fee should be generated for. This is sent to the API as the StudentType query parameter.",
  fields: [
    "ALL – Generate fee for all students (default)",
    "OLD – Generate fee for old (existing) students only",
    "NEW – Generate fee for new students only",
  ],
},
  {
    title: "Month Selection",
    color: "#7B1FA2",
    description: "After selecting a class, all 12 months (April to March) are displayed with their respective charge details. Check the months you want to include in fee generation.",
    fields: [
      "Months displayed: April through March (financial year order)",
      "Each month has an independent checkbox to include/exclude it",
      "At least one month must be selected before generating fees",
    ],
  },
  {
    title: "Charge Details per Month",
    color: "#388E3C",
    description: "Each month displays a table of charges associated with that class. Select the charges to include and optionally override the charge amount.",
    fields: [
      "Charge Name – Name of the fee charge",
      "Charge Type – Category of the charge (e.g., Monthly, Admission)",
      "Checkbox – Include/exclude this charge for the selected month",
      "Charge field – Editable amount input (pre-filled with existing value)",
      "At least one charge must be selected per selected month",
    ],
  },
  {
    title: "Generate Fee",
    color: "#F57C00",
    description: "Click 'Generate Fee' to submit the selected months and charges. A progress indicator is shown during submission.",
    fields: [
      "Requires 'Write' permission (uWrite = 1)",
      "Validates that at least one month is selected",
      "Validates that at least one charge is selected within selected months",
      "Shows circular progress (%) during API call",
      "Button disabled if no class/session selected or unauthorized",
    ],
  },
  {
    title: "Reset Fee",
    color: "#C62828",
    description: "Click 'Reset Fee' to reload the page and clear all selections.",
    fields: [
      "Reloads the entire page (window.location.reload)",
      "Clears all month and charge selections",
      "Button disabled if no class/session is selected",
    ],
  },
  {
    title: "Validation Modals",
    color: "#0277BD",
    description: "If required selections are missing, validation dialogs appear to guide the user before submission.",
    fields: [
      "No Month Selected – Prompts user to select at least one month",
      "No Charge Selected – Prompts user to select at least one charge",
    ],
  },
];

const studentTypeOptions = [
  { value: 'ALL', label: 'ALL' },
  { value: 'OLD', label: 'OLD' },
  { value: 'NEW', label: 'NEW' },
];

const FeeGeneration = () => {
  const [selectedStudentType, setSelectedStudentType] = useState('ALL');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // ── Info dialog state ──
  const [infoOpen, setInfoOpen] = useState(false);
  const [ddlClass, setDdlClass] = useState([]);
  const [ddlSession, setDdlSession] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [months, setMonths] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [chargeDetails, setChargeDetails] = useState([]);
  const [chargedValues, setChargedValues] = useState({});
  const [chargeDetailsByMonth, setChargeDetailsByMonth] = useState({});
  const [loading, setLoading] = useState(false);
  const [showNoChargeModal, setShowNoChargeModal] = useState(false);
  const [showNoMonthModal, setShowNoMonthModal] = useState(false);
  const [authorization, setAuthorization] = useState(null);
  const navigate = useNavigate();
  const { encodedFormId } = useParams();

  const monthsData = [
    { monthId: 4, month: "April" },
    { monthId: 5, month: "May" },
    { monthId: 6, month: "June" },
    { monthId: 7, month: "July" },
    { monthId: 8, month: "August" },
    { monthId: 9, month: "September" },
    { monthId: 10, month: "October" },
    { monthId: 11, month: "November" },
    { monthId: 12, month: "December" },
    { monthId: 1, month: "January" },
    { monthId: 2, month: "February" },
    { monthId: 3, month: "March" },
  ];

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    if (!formId) {
      console.error("formId is null, cannot call Authorizer");
      setAuthorization({ isPermission: 0 });
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });
      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData[0]);
      } else {
        console.error("Authorization failed");
        setAuthorization({ isPermission: 0 });
      }
    } catch (error) {
      console.error("API request error:", error);
      setAuthorization({ isPermission: 0 });
    }
  };

  const fetchFinancialYears = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/ClassPromotion/GetFinancialYear`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error(`Error fetching financial years: ${response.status}`);
      }
      const data = await response.json();
      setDdlSession(data);
      const today = new Date();
      const currentYear = today.getFullYear();
      const nextYear = currentYear + 1;
      const financialYearStart = new Date(currentYear, 3, 1);
      const financialYearEnd = new Date(nextYear, 2, 31);
      let currentSessionString;
      if (today >= financialYearStart && today <= financialYearEnd) {
        currentSessionString = `${currentYear}-${nextYear}`;
      } else {
        currentSessionString = `${currentYear - 1}-${currentYear}`;
      }
      const currentSession = data.find(
        (item) => item.finanacialYear === currentSessionString
      );
      if (currentSession) {
        setSelectedSession(currentSession.financialYearID);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchClass = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching classes: ${response.status}`);
      }
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setDdlClass(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMonthFee = async (sessionId, classId, monthId) => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/FeeGenerate/ddlMonthFee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          sessionId: sessionId,
          classId: classId,
          monthId: monthId,
        }),
      });
      if (!response.ok) {
        throw new Error(`Error fetching month fees: ${response.status}`);
      }
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setMonths(monthsData);
      setChargeDetailsByMonth(prevState => ({
        ...prevState,
        [monthId]: data.chargeList.map(charge => ({
          ...charge,
          checked: charge.status === 1,
          months: data.list.map(month => ({ monthId: month.monthId, checked: charge.status === 1 }))
        }))
      }));
      const initialChargedValues = {};
      data.chargeList.forEach(charge => {
        const monthIds = charge.monthId.split(',').filter(id => id !== '');
        monthIds.forEach(monthId => {
          initialChargedValues[`${monthId}-${charge.chargeId}`] = charge.charge;
        });
      });
      setChargedValues(prevState => ({
        ...prevState,
        ...initialChargedValues
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Authorizer();
    fetchFinancialYears();
    fetchClass();
  }, []);

  const handleClassChange = async (event) => {
    const selectedClass = event.target.value;
    setSelectedClass(selectedClass);
    setMonths([]);
    setChargeDetailsByMonth({});
    for (const month of monthsData) {
      await fetchMonthFee(selectedSession, selectedClass, month.monthId);
    }
  };

  const handleSessionChange = (event) => {
    setSelectedSession(event.target.value);
  };

  const handleStudentTypeChange = (event) => {
  setSelectedStudentType(event.target.value);
};

  const handleMonthCheckboxChange = (event, monthId) => {
    const isChecked = event.target.checked;
    const updatedMonths = months.map(month => month.monthId === monthId ? { ...month, isMonthChecked: isChecked } : month);
    setMonths(updatedMonths);
  };

  const handleChargeCheckboxChange = (event, chargeId, monthId) => {
    const isChecked = event.target.checked;
    setChargeDetailsByMonth(prevState => {
      const updatedState = { ...prevState };
      const updatedChargeDetails = updatedState[monthId].map(charge => {
        if (charge.chargeId === chargeId) {
          return {
            ...charge,
            months: charge.months.map(month =>
              month.monthId === monthId ? { ...month, checked: isChecked } : month
            )
          };
        }
        return charge;
      });
      updatedState[monthId] = updatedChargeDetails;
      return updatedState;
    });
  };

  const handleChargeInputChange = (event, chargeId, monthId) => {
    const value = event.target.value;
    const key = `${monthId}-${chargeId}`;
    setChargedValues(prevState => {
      const updatedValues = { ...prevState };
      if (value.trim() === '') {
        delete updatedValues[key];
      } else {
        updatedValues[key] = value;
      }
      return updatedValues;
    });
  };

  const handleReset = () => {
    window.location.reload();
  };

  const handleCloseNoChargeModal = () => {
    setShowNoChargeModal(false);
  };

  const handleCloseNoMonthModal = () => {
    setShowNoMonthModal(false);
  };

  const generateFee = async () => {
    if (authorization?.uWrite !== 1) {
      // alert("You are not authorized to generate fees.");
      setSnackbarMessage('You are not authorized to generate fees.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }

    const selectedMonths = months.filter(month => month.isMonthChecked);
    if (selectedMonths.length === 0) {
      setShowNoMonthModal(true);
      return;
    }

const anyChargeSelected = selectedMonths.some(month =>
  (chargeDetailsByMonth[month.monthId] || []).some(charge =>
    charge.months.some(monthDetail => monthDetail.monthId === month.monthId && monthDetail.checked) &&
    parseFloat(chargedValues[`${month.monthId}-${charge.chargeId}`] || 0) > 0
  )
);

if (!anyChargeSelected) {
  setShowNoChargeModal(true);
  return;
}

    try {
      const url = process.env.REACT_APP_BASE_URL;
const apiUrl = `${url}/FeeGenerate?StudentType=${encodeURIComponent(selectedStudentType)}`;
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const payload = selectedMonths.map(month => ({
        isMonthChecked: true,
        classId: selectedClass,
        sessionId: selectedSession,
        monthId: month.monthId,
        classCharge: (chargeDetailsByMonth[month.monthId] || [])
          .filter(charge =>
            charge.months.some(monthDetail => monthDetail.monthId === month.monthId && monthDetail.checked)
          )
          .map(charge => ({
            isClassChargeChecked: true,
            charge: chargedValues[`${month.monthId}-${charge.chargeId}`] || charge.charge,
            chargeId: charge.chargeId
          }))
      }));

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setLoadingBarProgress(0);
        // alert(`Error generating fee: ${response.status}`);
        setSnackbarMessage(`Error generating fee: ${response.status}`);
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      } else {
        setLoadingBarProgress(100);
        setTimeout(() => {
          setLoadingBarProgress(0);
          // alert('Fee generated successfully');
          setSnackbarMessage('Fee generated successfully');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
        }, 500);
      }
    } catch (error) {
      console.error(error);
      setLoadingBarProgress(0);
      // alert('Error generating fee');
      setSnackbarMessage('Error generating fee');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
    }
  };

  if (!authorization) {
    return <div>Loading...</div>;
  }

  if (authorization.isPermission !== 1 || !formId) {
    return <div>You are not authorized to view this page</div>;
  }

  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D", marginBottom: '15px' }}>
        <Toolbar>
          <Box
            sx={{
              backgroundColor: '#0B1F40', // dark navy blue
              padding: '12px 24px',
              boxShadow: 3,
              borderRadius: '4px 4px 0 0',
              marginBottom: '12px',
              width: "100%",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold', flexGrow: 1 }}>
              Generate Fee
            </Typography>

            {/* ── Info Button ── */}
            <IconButton
              color="inherit"
              onClick={() => setInfoOpen(true)}
              title="Page Information"
              style={{
                backgroundColor: "rgba(255,255,255,0.15)",
                border: "2px solid rgba(255,255,255,0.6)",
                borderRadius: "50%",
                padding: "6px",
              }}
            >
              <InfoIcon style={{ fontSize: 26 }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Info Dialog ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, padding: 0 } }}
      >
        <DialogTitle
          style={{
            backgroundColor: "#0B1F3D",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Fee Generation — Page Guide
            </Typography>
          </Box>
         
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            This page is used to generate fee records for a class and session. Select the class, choose months and charges, then click Generate Fee to submit.
          </Typography>

          {feeGenerationInfoSections.map((section, index) => (
            <Box
              key={index}
              style={{
                backgroundColor: "#fff",
                borderRadius: 12,
                padding: "18px 20px",
                marginBottom: 16,
                borderLeft: `5px solid ${section.color}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              }}
            >
              <Typography
                variant="h6"
                style={{ fontWeight: "bold", color: section.color, marginBottom: 6 }}
              >
                {`${index + 1}. ${section.title}`}
              </Typography>

              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                {section.description}
              </Typography>

              <Divider style={{ marginBottom: 10 }} />

              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 6 }}>
                📝 DETAILS
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {section.fields.map((field, i) => (
                  <Chip
                    key={i}
                    label={field}
                    size="small"
                    variant="outlined"
                    style={{
                      borderColor: section.color,
                      color: section.color,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            style={{ backgroundColor: "#0B1F3D", color: "#fff", borderRadius: 20, paddingInline: 28 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} sx={{ padding: 2, width: '100%', margin: 'auto', marginTop: 2 }}>
        <Typography variant="h5" align="center" gutterBottom style={{ marginBottom: '24px', fontWeight: 'bold' }}>
          Fee Generation
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, marginBottom: 1 }}>
          <TextField
            fullWidth
            select
            label="Class"
            variant="outlined"
            value={selectedClass}
            onChange={handleClassChange}
            sx={{ marginRight: 1 }}
          >
            {ddlClass.map((item) => (
              <MenuItem key={item.classId} value={item.classId}>
                {item.className}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label="Session"
            variant="outlined"
            value={selectedSession}
            onChange={handleSessionChange}
          >
            {ddlSession.map((year) => (
              <MenuItem key={year.financialYearID} value={year.financialYearID}>
                {year.finanacialYear}
              </MenuItem>
            ))}
          </TextField>

          <TextField
          disabled={loadingBarProgress > 0}
  fullWidth
  select
  label="Student Type"
  variant="outlined"
  value={selectedStudentType}
  onChange={handleStudentTypeChange}
  sx={{ marginLeft: 1 }}
>
  {studentTypeOptions.map((item) => (
    <MenuItem key={item.value} value={item.value}>
      {item.label}
    </MenuItem>
  ))}
</TextField>
        </Box>
      </Paper>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <CircularProgress />
        </div>
      )}
      {!loading && (
        <Paper elevation={3} sx={{ padding: 2, width: '100%', margin: 'auto', marginTop: 4 }}>
          <Typography variant="h6" align="center" gutterBottom style={{ marginBottom: '24px', fontWeight: 'bold' }}>
            Select Months
          </Typography>
          <Grid container spacing={2}>
            {months.map((month) => (
              <Grid item xs={6} key={month.monthId}>
                <Box sx={{ marginBottom: 2 }}>
                  <FormControlLabel
                    control={<Checkbox checked={month.isMonthChecked || false} onChange={(event) => handleMonthCheckboxChange(event, month.monthId)} />}
                    label={month.month}
                  />
                  <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
                    <Table sx={{ minWidth: 300 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell>Charge Name</TableCell>
                          <TableCell>Charge Type</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(chargeDetailsByMonth[month.monthId] || []).map((charge, chargeIndex) => (
                          <TableRow key={chargeIndex}>
                            <TableCell>
                              <Checkbox
                                checked={
                                  charge.months.find(monthDetail => monthDetail.monthId === month.monthId)?.checked || false
                                }
                                onChange={(event) => handleChargeCheckboxChange(event, charge.chargeId, month.monthId)}
                              />
                            </TableCell>
                            <TableCell>{charge.chargeName}</TableCell>
                            <TableCell>{charge.chargeType}</TableCell>
                            <TableCell>
                              <TextField
                                label="Charge"
                                variant="outlined"
                                size="small"
                                value={chargedValues[`${month.monthId}-${charge.chargeId}`] || ''}
                                onChange={(e) => handleChargeInputChange(e, charge.chargeId, month.monthId)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2} alignItems="center" sx={{ marginTop: '10px' }}>
            <Grid item xs={6}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="small"
                startIcon={<GenerateIcon />}
                sx={{ backgroundColor: '#2196f3', '&:hover': { backgroundColor: '#1976d2' } }}
                onClick={generateFee}
                disabled={!selectedClass || !selectedSession || loadingBarProgress > 0 || authorization.uWrite !== 1}
              >
                Generate Fee
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                size="small"
                startIcon={<ResetIcon />}
                sx={{ backgroundColor: '#f44336', '&:hover': { backgroundColor: '#d32f2f' } }}
                onClick={handleReset}
                disabled={!selectedClass || !selectedSession}
              >
                Reset Fee
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingBarProgress > 0}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress variant="determinate" value={loadingBarProgress} size={80} thickness={4} />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" component="div" color="white">
              {`${Math.round(loadingBarProgress)}%`}
            </Typography>
          </Box>
        </Box>
      </Backdrop>
      <Dialog open={showNoMonthModal} onClose={handleCloseNoMonthModal}>
        <DialogTitle>Validation Error</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please select at least one month to generate the fee.
          </DialogContentText>
          <DialogContentText>
  Please select at least one charge with an amount greater than 0 to generate the fee.
</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoMonthModal} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showNoChargeModal} onClose={handleCloseNoChargeModal}>
        <DialogTitle>Validation Error</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please select at least one charge to generate the fee.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoChargeModal} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>
    </Container>
  );
};

export default FeeGeneration;